import React from 'react';
import {Box, Chip} from '@mui/material';
import {formatCost} from '../../utils/format';

interface FieldAccuracy {
  correct: number;
  total: number;
}

interface BaselineStats {
  passed: number;
  totalCases: number;
  fieldAccuracy: {
    guideable: FieldAccuracy;
    topic: FieldAccuracy;
    audience: FieldAccuracy;
  };
}

interface EvalSummaryBarProps {
  totalCases: number;
  passed: number;
  failed: number;
  fieldAccuracy: {
    guideable: FieldAccuracy;
    topic: FieldAccuracy;
    audience: FieldAccuracy;
  };
  guideableScore?: number;
  overallScore?: number;
  weightedScore?: number | null;
  promptName?: string;
  modelName?: string;
  baseline?: BaselineStats;
  inputTokens?: number | null;
  outputTokens?: number | null;
  totalCost?: number | null;
}

function pct(correct: number, total: number): string {
  if (total === 0) return '—';
  return `${Math.round((correct / total) * 100)}%`;
}

function pctNum(correct: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((correct / total) * 100);
}

function DeltaChip({current, baseline}: {current: number; baseline: number}) {
  const delta = current - baseline;
  if (delta === 0) return null;
  const sign = delta > 0 ? '+' : '';
  const color = delta > 0 ? '#059669' : '#DC2626';
  const bg = delta > 0 ? '#ECFDF5' : '#FEF2F2';
  return (
    <Chip
      label={`${sign}${delta}%`}
      size="small"
      sx={{fontSize: 10, fontWeight: 700, color, bgcolor: bg, height: 20, ml: -0.5}}
    />
  );
}

export default function EvalSummaryBar({totalCases, passed, failed, fieldAccuracy, guideableScore, overallScore, weightedScore, promptName, modelName, baseline, inputTokens, outputTokens, totalCost}: EvalSummaryBarProps) {
  const overallPct = totalCases > 0 ? `${Math.round((passed / totalCases) * 100)}%` : '—';
  const overallNum = pctNum(passed, totalCases);
  const baselineOverallNum = baseline ? pctNum(baseline.passed, baseline.totalCases) : null;

  return (
    <Box sx={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: 1,
      p: 2,
      bgcolor: '#fff',
      borderRadius: '12px',
      border: '1px solid #E5E7EB',
      alignItems: 'center',
    }}>
      {promptName && (
        <Chip label={`Prompt: ${promptName}`} size="small" sx={{fontSize: 12, bgcolor: '#F3F4F6', fontWeight: 600, color: '#374151'}} />
      )}
      {modelName && (
        <Chip label={`Model: ${modelName}`} size="small" sx={{fontSize: 12, bgcolor: '#EFF6FF', fontWeight: 500, color: '#1E40AF'}} />
      )}
      {weightedScore != null && (
        <Chip label={`Weighted: ${weightedScore}%`} size="small" sx={{fontSize: 12, bgcolor: weightedScore >= 80 ? '#ECFDF5' : weightedScore >= 50 ? '#FFFBEB' : '#FEF2F2', color: weightedScore >= 80 ? '#065F46' : weightedScore >= 50 ? '#92400E' : '#991B1B', fontWeight: 700}} />
      )}
      {weightedScore == null && guideableScore !== undefined && (
        <Chip label={`Guideable: ${guideableScore}%`} size="small" sx={{fontSize: 12, bgcolor: guideableScore >= 80 ? '#ECFDF5' : guideableScore >= 50 ? '#FFFBEB' : '#FEF2F2', color: guideableScore >= 80 ? '#065F46' : guideableScore >= 50 ? '#92400E' : '#991B1B', fontWeight: 700}} />
      )}
      {baselineOverallNum !== null && <DeltaChip current={overallNum} baseline={baselineOverallNum} />}
      <Chip label={`${totalCases} cases`} size="small" sx={{fontSize: 12, bgcolor: '#F3F4F6', fontWeight: 500}} />
      <Chip label={`${passed} passed`} size="small" sx={{fontSize: 12, bgcolor: '#ECFDF5', color: '#065F46', fontWeight: 500}} />
      <Chip label={`${failed} failed`} size="small" sx={{fontSize: 12, bgcolor: failed > 0 ? '#FEF2F2' : '#F3F4F6', color: failed > 0 ? '#991B1B' : '#6B7280', fontWeight: 500}} />
      {(totalCost != null || (inputTokens != null && inputTokens > 0)) && (
        <>
          <Box sx={{borderLeft: '1px solid #E5E7EB', mx: 0.5}} />
          {totalCost != null && totalCost > 0 && (
            <Chip label={formatCost(totalCost)} size="small" sx={{fontSize: 12, bgcolor: '#F3F4F6', fontWeight: 600, color: '#374151'}} />
          )}
          {inputTokens != null && inputTokens > 0 && (
            <Chip label={`${((inputTokens + (outputTokens || 0)) / 1000).toFixed(0)}k tokens`} size="small" sx={{fontSize: 11, bgcolor: '#F3F4F6', color: '#6B7280'}} />
          )}
        </>
      )}
      <Box sx={{borderLeft: '1px solid #E5E7EB', mx: 0.5}} />
      <Chip label={`guideable: ${pct(fieldAccuracy.guideable.correct, fieldAccuracy.guideable.total)}`} size="small" variant="outlined" sx={{fontSize: 11}} />
      {baseline && <DeltaChip current={pctNum(fieldAccuracy.guideable.correct, fieldAccuracy.guideable.total)} baseline={pctNum(baseline.fieldAccuracy.guideable.correct, baseline.fieldAccuracy.guideable.total)} />}
      <Chip label={`topic: ${pct(fieldAccuracy.topic.correct, fieldAccuracy.topic.total)}`} size="small" variant="outlined" sx={{fontSize: 11}} />
      {baseline && <DeltaChip current={pctNum(fieldAccuracy.topic.correct, fieldAccuracy.topic.total)} baseline={pctNum(baseline.fieldAccuracy.topic.correct, baseline.fieldAccuracy.topic.total)} />}
      <Chip label={`audience: ${pct(fieldAccuracy.audience.correct, fieldAccuracy.audience.total)}`} size="small" variant="outlined" sx={{fontSize: 11}} />
      {baseline && <DeltaChip current={pctNum(fieldAccuracy.audience.correct, fieldAccuracy.audience.total)} baseline={pctNum(baseline.fieldAccuracy.audience.correct, baseline.fieldAccuracy.audience.total)} />}
    </Box>
  );
}
